import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { Copy, BookOpen, Lock } from "lucide-react";
import { toast } from "sonner";
import { TypeTag } from "../components/ArticleCard";
import { MaskLines, Reveal } from "../components/Motion";
import Newsletter from "../components/Newsletter";
import { useFetch } from "../lib/useFetch";
import { useAuth } from "../context/AuthContext";
import SelectionMenu from "../components/SelectionMenu";
import TranslateDialog from "../components/TranslateDialog";
import { useArticleTools, SaveButton, MarkedText, HighlightList } from "../components/ArticleTools";

function Paywall({ slug }) {
  const { user } = useAuth();
  return (
    <div className="relative !mt-0 f-sans" data-testid="article-paywall">
      <div className="absolute -top-40 inset-x-0 h-40 bg-gradient-to-b from-transparent to-[var(--paper)] pointer-events-none" />
      <div className="bg-[var(--ink)] text-white p-8 sm:p-10">
        <p className="eyebrow !text-[#C3CAD9] flex items-center gap-2"><Lock size={13} /> Subscriber-only research</p>
        <p className="f-serif text-3xl sm:text-4xl mt-4 leading-tight">Keep reading this peer-reviewed study.</p>
        <p className="text-[#D5DAE4] mt-3 leading-relaxed">{user ? "Your account doesn't have an active plan yet. Subscribe to unlock the full study and the research archive." : "Subscribers get every study in full, citation tools and the complete issues in the online reader."}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/subscribe" className="bg-[var(--red)] text-white h-12 px-6 inline-flex items-center font-semibold hover:bg-white hover:text-[var(--ink)] transition-colors" data-testid="paywall-subscribe-btn">See subscription plans</Link>
          {!user && <Link to={`/account?next=/article/${slug}`} className="border border-white/40 h-12 px-6 inline-flex items-center font-semibold hover:border-white transition-colors" data-testid="paywall-signin-btn">I already subscribe — sign in</Link>}
        </div>
      </div>
    </div>
  );
}

function cite(a, style) {
  const y = (a.created_at || "2026").slice(0, 4);
  const doi = a.doi ? ` https://doi.org/${a.doi}` : "";
  if (style === "MLA") return `${a.author}. "${a.title}." Business Performance Journal, ${y}.${doi}`;
  if (style === "Chicago") return `${a.author}. "${a.title}." Business Performance Journal (${y}).${doi}`;
  return `${a.author} (${y}). ${a.title}. Business Performance Journal.${doi}`;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { data: a, loading } = useFetch(`/articles/${slug}?u=${user?.id || ""}`, null);
  const { scrollYProgress } = useScroll();
  const [style, setStyle] = useState("APA");
  const [tr, setTr] = useState("");
  const bodyRef = useRef(null);
  const nav = useNavigate();
  const tools = useArticleTools(slug);

  if (loading) return <div className="h-[60vh]" />;
  if (!a) return <div className="px-5 lg:px-12 py-32 text-center"><p className="f-serif text-4xl">Article not found.</p><Link to="/articles" className="link-u mt-6 inline-block">Back to the library</Link></div>;

  const body = Array.isArray(a.body) ? a.body : [];
  const copy = () => { navigator.clipboard?.writeText(cite(a, style)); toast.success(`${style} citation copied`); };

  return (
    <article data-testid="article-detail">
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-[3px] bg-[var(--red)] origin-left z-50" />
      <header className="px-5 lg:px-12 pt-14 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8"><TypeTag type={a.type} /><span className="eyebrow">· {a.category}</span></div>
        <h1 className="f-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="article-title"><MaskLines lines={[a.title]} /></h1>
        <Reveal delay={0.4}><p className="f-serif italic text-xl sm:text-2xl text-[var(--ink-2)] mt-8 leading-snug max-w-3xl">{a.dek}</p></Reveal>
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 border-t rule pt-5 f-mono text-xs text-[var(--muted)]">
          <span className="text-[var(--ink)]">{a.author}</span><span>{a.affiliation}</span><span>{a.read_time} min read</span>
          {a.doi && <span data-testid="article-doi">DOI {a.doi}</span>}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <SaveButton saved={tools.saved} onClick={tools.toggleSave} />
          <span className="text-xs text-[var(--muted)]">Tip: select any text to highlight, translate or search it.</span>
        </div>
      </header>
      <Reveal className="px-5 lg:px-12 mt-12 max-w-6xl mx-auto"><div className="aspect-[16/8] overflow-hidden"><img src={a.image} alt="" className="w-full h-full object-cover" /></div></Reveal>
      <div className="px-5 lg:px-12 mt-16 max-w-6xl mx-auto grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-28 space-y-8">
            <div>
              <p className="eyebrow mb-3">Cite this</p>
              <div className="flex gap-1 mb-3">{["APA", "MLA", "Chicago"].map((s) => <button key={s} onClick={() => setStyle(s)} data-testid={`cite-style-${s.toLowerCase()}`} className={`px-2.5 h-7 text-xs border ${style === s ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "rule"}`}>{s}</button>)}</div>
              <p className="text-xs text-[var(--ink-2)] leading-relaxed" data-testid="citation-text">{cite(a, style)}</p>
              <button onClick={copy} className="mt-3 text-xs flex items-center gap-1.5 link-u" data-testid="copy-citation-btn"><Copy size={13} /> Copy citation</button>
            </div>
            {a.issue_id && <Link to={`/reader/${a.issue_id}`} className="flex items-center gap-2 text-sm font-semibold link-u" data-testid="article-open-issue"><BookOpen size={16} /> Read in the issue</Link>}
            <HighlightList highlights={tools.highlights} onRemove={tools.removeHighlight} />
          </div>
        </aside>
        <div ref={bodyRef} className="lg:col-span-7 order-1 lg:order-2 text-[1.13rem] leading-[1.8] text-[var(--ink-2)] space-y-7 f-serif" data-testid="article-body">
          {body.map((p, i) => <p key={i} data-para={i} className={i === 0 ? "first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-[var(--red)]" : ""}><MarkedText text={p} marks={tools.highlights.filter((h) => h.para === i)} /></p>)}
          {a.locked && <Paywall slug={a.slug} />}
          <div className="!mt-16 border border-[var(--ink)] p-8 f-sans">
            <p className="f-serif text-2xl text-[var(--ink)]">Enjoyed this? Get one idea like it every Monday.</p>
            <div className="mt-6"><Newsletter /></div>
          </div>
        </div>
      </div>
      <SelectionMenu containerRef={bodyRef} onHighlight={tools.addHighlight} onTranslate={(s) => setTr(s.text)} onSearch={(s) => nav(`/articles?q=${encodeURIComponent(s.text.slice(0, 80))}`)} />
      <TranslateDialog text={tr} onClose={() => setTr("")} />
    </article>
  );
}
